import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleGenAI } from '@google/genai';
import { Subject, Topic, Progress, QuizQuestion } from './types';
import { GradeSelection } from './components/GradeSelection';
import { Dashboard } from './components/Dashboard';
import { TopicsList } from './components/TopicsList';
import { LessonView } from './components/LessonView';
import { QuizView } from './components/QuizView';
import { QuizResults } from './components/QuizResults';
import { BottomNav } from './components/BottomNav';

const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY || '' });

// Get the API URL dynamically (works on local network)
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // If we are on localhost or a local network IP (like 192.168.x.x), use the local backend
  if (
    hostname === 'localhost' || 
    hostname === '127.0.0.1' || 
    hostname.startsWith('192.168.') || 
    hostname.startsWith('10.')
  ) {
    // Dynamically use the same hostname so mobile devices can reach the local PHP server
    return `http://${hostname}:8000/api`;
  }
  return `https://devapi.milestoneschoolzambia.com/api`;
};

const API_BASE_URL = getApiUrl();

// Pre-configured axios with timeout — prevents indefinite hanging
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000
});

// Simple client-side cache to avoid re-fetching identical data
const clientCache: Record<string, { data: any; timestamp: number }> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCached = (key: string) => {
  const entry = clientCache[key];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.data;
  }
  return null;
};

const setCache = (key: string, data: any) => {
  clientCache[key] = { data, timestamp: Date.now() };
};

// Helper function to clean HTML content - preserves educational structure
const cleanHtmlContent = (html: string) => {
  if (!html) return '<p>Content loading...</p>';

  let cleaned = html;

  // Remove excessive heading tags but keep h3 for structure
  cleaned = cleaned.replace(/<h[1-2][^>]*>.*?<\/h[1-2]>/gi, '');

  // Convert markdown bold to HTML strong tags
  cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Clean up empty paragraphs
  cleaned = cleaned.replace(/<p>\s*<\/p>/g, '');

  return cleaned;
};

// Map form to Zambian grade level (Forms 1-4)
const getZambianFormLevel = (grade: number): string => {
  if (grade >= 10 && grade <= 12) return 'Senior Secondary';
  if (grade >= 8 && grade <= 9) return 'Junior Secondary';
  return `Form ${grade}`;
};

// Main App Component
function App() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync state with URL params - using 'form' instead of 'grade' for Zambian context
  const urlForm = searchParams.get('form') ? parseInt(searchParams.get('form')!) : null;
  const urlView = (searchParams.get('view') as 'dashboard' | 'topics' | 'lesson' | 'quiz') || 'dashboard';
  const urlSubjectId = searchParams.get('subject') || null;
  const urlTopicId = searchParams.get('topic') || null;

  // Form selection state (Form 1-4 in Zambian secondary)
  const [hasSelectedForm, setHasSelectedForm] = useState(!!urlForm);

  // Navigation state
  const [currentView, setCurrentView] = useState<'dashboard' | 'topics' | 'lesson' | 'quiz'>(urlView);

  // Data state
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [progress, setProgress] = useState<Progress[]>([]);

  // Selection state
  const [selectedForm, setSelectedForm] = useState<number | null>(urlForm);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);

  // Lesson and quiz state
  const [lessonContent, setLessonContent] = useState<string>('');
  const [aiContent, setAiContent] = useState<string>('');
  const [practicalActivities, setPracticalActivities] = useState<any[]>([]);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<number[]>([]);
  const [quizResult, setQuizResult] = useState<any>(null);

  // Competence tracking
  const [developedCompetences, setDevelopedCompetences] = useState<string[]>([]);

  // Helper to update URL
  const updateRoute = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  // UI state
  const [loading, setLoading] = useState(true);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load subjects after form is selected
  useEffect(() => {
    if (hasSelectedForm && selectedForm) {
      loadDashboardData();
    }
  }, [hasSelectedForm, selectedForm]);

  // Restore state from URL on load and data fetch
  useEffect(() => {
    if (subjects.length > 0 && urlSubjectId && !selectedSubject) {
      const subject = subjects.find(s => s.id.toString() === urlSubjectId || s.name === urlSubjectId);
      if (subject) {
        setSelectedSubject(subject);
        loadTopics(subject, false);
      }
    }
  }, [subjects, urlSubjectId]);

  useEffect(() => {
    if (topics.length > 0 && urlTopicId && !selectedTopic) {
      const topic = topics.find(t => t.id.toString() === urlTopicId);
      if (topic) {
        setSelectedTopic(topic);
        if (urlView === 'lesson') {
          loadLesson(topic, false);
        } else if (urlView === 'quiz') {
          startQuiz(topic, false);
        }
      }
    }
  }, [topics, urlTopicId, urlView]);

  // Load dashboard data
  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const cachedSubjects = getCached(`subjects_form${selectedForm}`);
      const cachedProgress = getCached(`progress_form${selectedForm}`);

      if (cachedSubjects && cachedProgress) {
        setSubjects(cachedSubjects);
        setProgress(cachedProgress);
        setCurrentView('dashboard');
        setLoading(false);
        return;
      }

      console.log('Connecting to API:', API_BASE_URL);
      console.log(`Loading Form ${selectedForm} curriculum data...`);

      const [subjectsRes, progressRes] = await Promise.all([
        api.get('/subjects', { params: { form: selectedForm } }),
        api.get('/progress', { params: { form: selectedForm } })
      ]);
      setSubjects(subjectsRes.data);
      setProgress(progressRes.data);
      setCache(`subjects_form${selectedForm}`, subjectsRes.data);
      setCache(`progress_form${selectedForm}`, progressRes.data);
      setCurrentView('dashboard');
    } catch (error: any) {
      console.error('Error loading data:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. The server may be starting up — please retry in a few seconds.');
      } else {
        setError(`Cannot connect to server. Make sure backend is running on ${API_BASE_URL}`);
      }
    }
    setLoading(false);
  };

  // Load topics for a subject - aligned with Zambian syllabus structure
  const loadTopics = async (subject: Subject, updateUrl = true) => {
    setSelectedSubject(subject);
    setCurrentView('topics');

    if (updateUrl) {
      updateRoute({ subject: subject.id.toString(), view: 'topics', topic: null });
    }

    if (isMobile) {
      setSidebarOpen(false);
    }

    const cacheKey = `topics:${subject.name}:form${selectedForm}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setTopics(cached);
      return;
    }

    setLoading(true);
    try {
      const topicsRes = await api.get('/topics', {
        params: { subject: subject.name, form: selectedForm }
      });
      setTopics(topicsRes.data);
      setCache(cacheKey, topicsRes.data);
    } catch (error) {
      console.error('Error loading topics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load lesson content with Zambian context
  const loadLesson = async (topic: Topic, updateUrl = true) => {
    setSelectedTopic(topic);
    setCurrentView('lesson');

    if (updateUrl) {
      updateRoute({ topic: topic.id.toString(), view: 'lesson' });
    }

    const cacheKey = `topic:${topic.id}:form${selectedForm}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setLessonContent(cleanHtmlContent(cached.content));
      setAiContent(cached.ai_content ? cleanHtmlContent(cached.ai_content) : '');
      setPracticalActivities(cached.practical_activities || []);
      setDevelopedCompetences(cached.competences || []);
      return;
    }

    setLoading(true);
    try {
      const topicRes = await api.get(`/topics/${topic.id}`, {
        params: { form: selectedForm }
      });
      setLessonContent(cleanHtmlContent(topicRes.data.content));
      setAiContent(topicRes.data.ai_content ? cleanHtmlContent(topicRes.data.ai_content) : '');
      setPracticalActivities(topicRes.data.practical_activities || []);
      setDevelopedCompetences(topicRes.data.competences || []);
      setCache(cacheKey, {
        content: topicRes.data.content,
        ai_content: topicRes.data.ai_content,
        practical_activities: topicRes.data.practical_activities,
        competences: topicRes.data.competences
      });
    } catch (error) {
      console.error('Error loading lesson:', error);
    } finally {
      setLoading(false);
    }
  };

  // Go back to dashboard
  const goToDashboard = () => {
    setSelectedSubject(null);
    setSelectedTopic(null);
    setCurrentView('dashboard');
    setTopics([]);
    setQuizResult(null);
    setQuizQuestions([]);
    setPracticalActivities([]);
    setDevelopedCompetences([]);
    setAiContent('');

    updateRoute({ view: 'dashboard', subject: null, topic: null });

    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Generate AI explanation aligned with Zambian competence-based curriculum
  const generateAIExplanation = async () => {
    if (!selectedTopic) return;

    setAiGenerating(true);
    setAiContent(`
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px; color: #6b7280;">
        <div class="ai-loader"></div>
        <p style="margin-top: 20px; font-style: italic;">The AI Teacher is preparing a simpler explanation for you...</p>
        <p style="margin-top: 10px; font-size: 14px;">This will break down complex concepts into easy-to-understand Zambian examples!</p>
        <style>
          .ai-loader {
            width: 50px;
            height: 50px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #15803d;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        </style>
      </div>
    `);

    try {
      const topicContent = (lessonContent || '').replace(/<[^>]*>?/gm, '').substring(0, 2000);

      const systemInstruction = `You are an experienced Zambian teacher implementing the COMPETENCE-BASED CURRICULUM for Form ${selectedForm} students (ages ${selectedForm === 1 ? '13-14' : selectedForm === 2 ? '14-15' : selectedForm === 3 ? '15-16' : '16-17'}).

YOUR PRIMARY GOAL:
Take the provided topic content and explain it in a MUCH SIMPLER, MORE UNDERSTANDABLE way. Break down complex terms, use relatable analogies from daily Zambian life, and ensure any student can grasp the core concepts easily.

CURRICULUM PHILOSOPHY:
- Learners are not empty vessels - they come with prior knowledge
- Focus on what learners can DO (practical competences), not just what they know
- Encourage hands-on experiences, observation, questioning, and experimentation
- Connect learning to climate change awareness and environmental sustainability
- Use group work, investigations, and project-based learning

YOUR EXPLANATION MUST DEVELOP THESE KEY COMPETENCES:
- Analytical Thinking: Identify patterns, compile data, evaluate solutions
- Critical Thinking: Compare, classify, explore, solve problems
- Problem Solving: Make connections, use patterns to solve problems
- Collaboration: Work in groups, share ideas
- Creativity & Innovation: Explore, create, innovate
- Environmental Sustainability: Practice good environmental management

LESSON STRUCTURE (use these exact headings):
1. <h3>Let's Explore</h3>: Start with a real Zambian scenario or problem that creates curiosity (e.g., "Imagine you're at Soweto Market in Lusaka...")

2. <h3>🔬 Hands-On Investigation</h3>: A simple experiment or observation the student can do (e.g., "Take a leaf from your garden and observe...")

3. <h3>📚 Concept Overview</h3>: Main content explained clearly with local examples (farming in Mpika, fishing in Mpulungu, markets in Lusaka, Copperbelt industries)

4. <h3>💡 Competence Check</h3>: Questions that test what the learner can DO (not just recall)

5. <h3>🏆 Practical Challenge</h3>: A real-world task applying the concept to Zambian life

6. <h3>🌍 Connecting to Zambia's Environment</h3>: Link to climate change, conservation, or sustainability

STRICT RULES:
- NO greetings (no "Hello", "Welcome", "Dear student")
- NO concluding remarks (no "Good luck", "Class dismissed")
- Use only <p>, <h3>, <ul>, <li>, <div class="activity">, <div class="investigation"> tags
- Include simple SVG diagrams in a <div class="visual-aid"> with stroke="green" for Zambian theme
- Return ONLY valid JSON`;

      const prompt = `Please explain the following topic in a SIMPLER, MORE UNDERSTANDABLE way for Form ${selectedForm} Zambian students. Break down the content so it is very easy to grasp.
      
Subject context: ${selectedSubject?.name || 'General'}
Topic: "${selectedTopic.title}"
Original Content to Simplify: ${topicContent}

Return JSON format:
{
    "explanation": "<h3>Let's Explore</h3><p>Zambian scenario...</p><h3>🔬 Hands-On Investigation</h3><div class='investigation'><strong>Materials:</strong> ...<br><strong>Steps:</strong> ...</div><h3>📚 Concept Overview</h3><p>Content...</p><div class='visual-aid'><svg>...</svg></div><h3>💡 Competence Check</h3><ul><li>Question 1? Think about...</li></ul><h3>🏆 Practical Challenge</h3><p>Real task...</p><h3>🌍 Connecting to Zambia's Environment</h3><p>Environmental link...</p>",
    "competences_developed": ["Analytical Thinking", "Critical Thinking", "Problem Solving"],
    "practical_activities": [
        {
            "title": "Activity name",
            "materials": ["item1", "item2"],
            "steps": ["step1", "step2"],
            "expected_outcome": "What student will demonstrate"
        }
    ],
    "questions": [
        {
            "question": "Question text?",
            "options": ["A", "B", "C", "D"],
            "correct_answer": 0,
            "explanation": "Why it's correct",
            "competence_tested": "Analytical Thinking"
        }
    ]
}`;

      const result = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: prompt,
        config: {
          systemInstruction: systemInstruction,
          temperature: 0.3,
          maxOutputTokens: 4000,
          responseMimeType: "application/json",
        }
      });

      const responseText = result.text;
      if (!responseText) throw new Error("No response generated from AI");
      const responseData = JSON.parse(responseText);

      if (responseData.explanation) {
        setAiContent(cleanHtmlContent(responseData.explanation));
      }

      if (responseData.practical_activities) {
        setPracticalActivities(responseData.practical_activities);
      }

      if (responseData.competences_developed) {
        setDevelopedCompetences(responseData.competences_developed);
      }

      if (responseData.questions && responseData.questions.length > 0) {
        setQuizQuestions(responseData.questions);
      }

      try {
        await api.post(`/topics/${selectedTopic.id}/save_ai`, {
          content: responseData.explanation,
          practical_activities: responseData.practical_activities,
          competences: responseData.competences_developed,
          questions: responseData.questions,
          form: selectedForm
        });

        const updatedTopic = { ...selectedTopic, ai_content: responseData.explanation };
        setSelectedTopic(updatedTopic);

        const cacheKey = `topics:${selectedSubject?.name}:form${selectedForm}`;
        delete clientCache[cacheKey];
        delete clientCache[`topic:${selectedTopic.id}:form${selectedForm}`];
      } catch (err) {
        console.error('Failed to save AI content to database:', err);
      }
    } catch (error: any) {
      console.error('AI generation failed:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setAiContent('<p>⚠️ AI generation timed out. Please try again.</p>');
      } else {
        setAiContent('<p>⚠️ AI generation failed. Please try again.</p>');
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // Start quiz
  const startQuiz = async (topic = selectedTopic, updateUrl = true) => {
    if (!topic) return;

    if (updateUrl) {
      updateRoute({ view: 'quiz', topic: topic.id.toString() });
    }

    setLoading(true);

    try {
      if (quizQuestions.length === 0 || selectedTopic?.id !== topic.id) {
        const quizRes = await api.get(`/quiz/${topic.id}`, {
          params: { form: selectedForm }
        });
        if (quizRes.data.questions && quizRes.data.questions.length > 0) {
          setQuizQuestions(quizRes.data.questions);
        } else {
          setQuizQuestions([{
            id: 1,
            question: `What practical skills can you demonstrate related to ${topic.title}?`,
            options: ['Remembering facts', 'Applying to real life', 'Working in a team', 'Creating something new'],
            explanation: 'Select the competence-based answer about what you can DO with this knowledge.'
          }]);
        }
      }

      setAnswers(new Array(quizQuestions.length).fill(-1));
      setCurrentView('quiz');
      setQuizResult(null);
    } catch (error) {
      console.error('Error loading quiz:', error);
      alert('Error loading quiz questions');
    } finally {
      setLoading(false);
    }
  };

  // Submit quiz answers
  const submitQuiz = async () => {
    if (answers.includes(-1)) {
      alert('Please answer all questions before submitting!');
      return;
    }

    setLoading(true);

    try {
      const result = await api.post('/quiz/submit', {
        topic_id: selectedTopic!.id,
        answers: answers,
        form: selectedForm
      });
      setQuizResult(result.data);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Error submitting quiz');
    } finally {
      setLoading(false);
    }
  };

  // Get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 75) return '#15803d';
    if (score >= 50) return '#f59e0b';
    return '#dc2626';
  };

  // Get progress for a subject
  const getProgressForSubject = (subjectName: string) => {
    return progress.find(p => p.subject === subjectName);
  };

  // Handle form selection (Form 1-4, the Zambian secondary school structure)
  const handleFormSelect = (form: number) => {
    setSelectedForm(form);
    setHasSelectedForm(true);
    updateRoute({ form: form.toString(), view: 'dashboard' });
  };

  // ==================== FORM SELECTION SCREEN ====================
  if (!hasSelectedForm) {
    return <GradeSelection handleGradeSelect={handleFormSelect} isZambianSecondary={true} />;
  }

  // ==================== LOADING SCREEN ====================
  if (loading && currentView === 'dashboard') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '3px solid #dcfce7', borderTopColor: '#15803d', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
          <p style={{ color: '#166534' }}>Loading Form {selectedForm} Zambian Curriculum...</p>
        </div>
      </div>
    );
  }

  // ==================== ERROR SCREEN ====================
  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4', padding: '20px' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '500px', textAlign: 'center' }}>
          <h2 style={{ color: '#dc2626', marginBottom: '12px' }}>Connection Error</h2>
          <p style={{ color: '#6b7280', marginBottom: '20px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '12px 24px', background: '#15803d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ==================== MAIN APP LAYOUT ====================
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0fdf4' }}>
      {/* SIDEBAR */}
      <div style={{
        width: sidebarOpen ? '280px' : (isMobile ? '0px' : '80px'),
        background: 'white',
        borderRight: '1px solid #dcfce7',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'hidden',
        zIndex: 150,
        flexShrink: 0
      }}>
        {/* Sidebar Header */}
        <div style={{ padding: '20px 16px', borderBottom: '1px solid #dcfce7', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {!isMobile && sidebarOpen && <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#15803d' }}>Zambia Smart Learning</span>}
              {!isMobile && !sidebarOpen && <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#15803d' }}>ZSL</span>}
            </div>
            {!isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
              >
                {sidebarOpen ? '◀' : '▶'}
              </button>
            )}
            {isMobile && sidebarOpen && (
              <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            )}
          </div>
        </div>

        {/* Dashboard Button */}
        <div style={{ padding: '16px', borderBottom: '1px solid #dcfce7', flexShrink: 0 }}>
          <button
            onClick={goToDashboard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '12px',
              background: currentView === 'dashboard' ? '#f0fdf4' : 'transparent',
              border: `1px solid ${currentView === 'dashboard' ? '#15803d' : '#dcfce7'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <span style={{ fontSize: '20px' }}>🏠</span>
            {sidebarOpen && <span style={{ fontSize: '14px', fontWeight: '500' }}>Dashboard</span>}
          </button>
        </div>

        {/* Form Display */}
        <div style={{ padding: '16px', borderBottom: '1px solid #dcfce7', flexShrink: 0 }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{sidebarOpen ? 'Current Form' : '📖'}</div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            background: '#f0fdf4',
            borderRadius: '10px'
          }}>
            <strong>Form {selectedForm}</strong>
            {sidebarOpen && (
              <button
                onClick={() => {
                  setHasSelectedForm(false);
                  setSelectedSubject(null);
                  setTopics([]);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#15803d' }}
              >
                Change
              </button>
            )}
          </div>
        </div>

        {/* Subjects Navigation */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <h3 style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px', letterSpacing: '0.5px' }}>
            {sidebarOpen ? 'SUBJECTS (Zambian Curriculum)' : '📚'}
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {subjects.map(subject => {
              const progressData = getProgressForSubject(subject.name);
              return (
                <li key={subject.id} style={{ marginBottom: '4px' }}>
                  <button
                    onClick={() => loadTopics(subject)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px',
                      border: 'none',
                      background: selectedSubject?.id === subject.id ? '#f0fdf4' : 'transparent',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      justifyContent: !sidebarOpen ? 'center' : 'flex-start'
                    }}
                    title={!sidebarOpen ? subject.name : ''}
                  >
                    <span style={{ fontSize: '20px' }}>{subject.icon}</span>
                    {sidebarOpen && (
                      <>
                        <span style={{ flex: 1, textAlign: 'left', fontSize: '14px', fontWeight: selectedSubject?.id === subject.id ? '600' : '400' }}>
                          {subject.name}
                        </span>
                        {progressData && (
                          <span style={{ fontSize: '11px', color: getScoreColor(progressData.mastery_score), fontWeight: '500' }}>
                            {Math.round(progressData.mastery_score)}%
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Sidebar Footer - Competence-Based Message */}
        <div style={{ padding: '16px', borderTop: '1px solid #dcfce7', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: !sidebarOpen ? 'center' : 'flex-start' }}>
            <span style={{ width: '8px', height: '8px', background: '#15803d', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
            {sidebarOpen && <span style={{ fontSize: '12px', color: '#6b7280' }}>Competence-Based Learning</span>}
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 140
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* MAIN CONTENT AREA */}
      <div style={{ flex: 1, width: '100%', minWidth: 0, overflowY: 'auto', height: '100vh' }}>
        {/* Header */}
        <header style={{
          background: 'white',
          borderBottom: '1px solid #dcfce7',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {!isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ☰
              </button>
            )}
            <h1 style={{ fontSize: '18px', margin: 0 }}>
              Form {selectedForm} • {currentView === 'dashboard' ? 'Dashboard' : (selectedSubject ? selectedSubject.name : 'Smart Learning')}
              {selectedTopic && <span style={{ fontWeight: 'normal', color: '#6b7280' }}> / {selectedTopic.title}</span>}
            </h1>
          </div>
          {!isMobile && (
            <button
              onClick={goToDashboard}
              style={{
                background: currentView === 'dashboard' ? '#15803d' : '#f0fdf4',
                color: currentView === 'dashboard' ? 'white' : '#15803d',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🏠 Dashboard
            </button>
          )}
        </header>

        {/* Content Area */}
        <div style={{
          padding: '20px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          paddingBottom: isMobile ? '100px' : '20px'
        }}>

          {/* DASHBOARD VIEW */}
          {currentView === 'dashboard' && (
            <Dashboard
              selectedForm={selectedForm}
              subjects={subjects}
              progress={progress}
              loadTopics={loadTopics}
              getProgressForSubject={getProgressForSubject}
              getScoreColor={getScoreColor}
            />
          )}

          {/* TOPICS VIEW */}
          {currentView === 'topics' && (
            <TopicsList
              selectedSubject={selectedSubject}
              topics={topics}
              loading={loading}
              loadLesson={loadLesson}
            />
          )}

          {/* LESSON VIEW */}
          {currentView === 'lesson' && (
            <LessonView
              selectedTopic={selectedTopic}
              lessonContent={lessonContent}
              aiContent={aiContent}
              practicalActivities={practicalActivities}
              developedCompetences={developedCompetences}
              loading={loading}
              aiGenerating={aiGenerating}
              setCurrentView={setCurrentView}
              generateAIExplanation={generateAIExplanation}
              startQuiz={startQuiz}
            />
          )}

          {/* QUIZ VIEW */}
          {currentView === 'quiz' && selectedTopic && !quizResult && (
            <QuizView
              selectedTopic={selectedTopic}
              quizQuestions={quizQuestions}
              answers={answers}
              setAnswers={setAnswers}
              submitQuiz={submitQuiz}
              loading={loading}
              setCurrentView={setCurrentView}
            />
          )}

          {/* QUIZ RESULTS VIEW */}
          {currentView === 'quiz' && quizResult && (
            <QuizResults
              quizResult={quizResult}
              setQuizResult={setQuizResult}
              setCurrentView={setCurrentView}
              startQuiz={startQuiz}
            />
          )}
        </div>

        {isMobile && (
          <BottomNav
            currentView={currentView}
            goToDashboard={goToDashboard}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      </div>

      {/* Global Styles */}
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #f0fdf4; border-radius: 4px; }
        ::-webkit-scrollbar-thumb { background: #86efac; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #15803d; }
        
        .investigation, .activity {
          background: #f0fdf4;
          border-left: 4px solid #15803d;
          padding: 16px;
          margin: 16px 0;
          border-radius: 8px;
        }
        
        .visual-aid {
          text-align: center;
          margin: 20px 0;
          padding: 20px;
          background: #fafafa;
          border-radius: 12px;
        }
      `}</style>
    </div>
  );
}

export default App;